import { useCallback, useEffect, useState } from 'react'
import { CompanyWithInvoiceCode } from '../types'

export const usePropertyBulkSelection = () => {
    const [bulkMode, setBulkMode] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
    const [selectedById, setSelectedById] = useState<
        Map<number, CompanyWithInvoiceCode>
    >(() => new Map())

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set())
        setSelectedById(new Map())
    }, [])

    const exitBulkMode = useCallback(() => {
        setBulkMode(false)
        clearSelection()
    }, [clearSelection])

    const enterBulkMode = useCallback(() => {
        setBulkMode(true)
    }, [])

    const toggleBulkMode = useCallback(() => {
        if (bulkMode) {
            exitBulkMode()
        } else {
            enterBulkMode()
        }
    }, [bulkMode, enterBulkMode, exitBulkMode])

    const toggleSelection = useCallback(
        (company: CompanyWithInvoiceCode, isSelected: boolean) => {
            setSelectedIds((prev) => {
                const next = new Set(prev)
                if (isSelected) {
                    next.add(company.id)
                } else {
                    next.delete(company.id)
                }
                return next
            })
            setSelectedById((prev) => {
                const next = new Map(prev)
                if (isSelected) {
                    next.set(company.id, company)
                } else {
                    next.delete(company.id)
                }
                return next
            })
        },
        [],
    )

    const isSelected = useCallback((id: number) => selectedIds.has(id), [
        selectedIds,
    ])

    const selectedCount = selectedIds.size

    const selectedProperties = useCallback(
        () => Array.from(selectedById.values()),
        [selectedById],
    )

    const selectedWorkspaceIds = useCallback(() => Array.from(selectedIds), [
        selectedIds,
    ])

    const selectAllMatching = useCallback(
        (companies: CompanyWithInvoiceCode[]) => {
            const nextIds = new Set<number>()
            const nextById = new Map<number, CompanyWithInvoiceCode>()
            companies.forEach((company) => {
                nextIds.add(company.id)
                nextById.set(company.id, company)
            })
            setSelectedIds(nextIds)
            setSelectedById(nextById)
        },
        [],
    )

    const updateSelectedStatuses = useCallback(
        (status: CompanyWithInvoiceCode['status']) => {
            setSelectedById((prev) => {
                const next = new Map(prev)
                next.forEach((company, id) => {
                    next.set(id, { ...company, status })
                })
                return next
            })
        },
        [],
    )

    useEffect(() => {
        return () => {
            clearSelection()
            setBulkMode(false)
        }
    }, [clearSelection])

    return {
        bulkMode,
        selectedCount,
        toggleBulkMode,
        enterBulkMode,
        exitBulkMode,
        clearSelection,
        toggleSelection,
        isSelected,
        selectedProperties,
        selectedWorkspaceIds,
        selectAllMatching,
        updateSelectedStatuses,
    }
}
